import React from 'react';
import axios, { AxiosError, AxiosResponse } from 'axios';
import { getCustomHeader } from '../../common/clerkAuth';
import { useClerkErrorHandler } from '../../common/clerkErrorHandling';
import Logger from '@common/logger';
import globals from '@common/globals';

const logger = Logger('clerkApiInterceptor');

/**
 * Get the base URL for a given API name
 * Maps API names to their configured endpoints
 * @param apiName - The API name (from globals)
 * @returns string - The base URL for the API
 */
const getApiBaseUrl = (apiName: string): string => {
  // Map API names to their base URLs from globals
  switch (apiName) {
    case globals.RECIPE_API_NAME:
      return globals.RECIPE_API;
    case globals.ORGANIZATION_API_NAME:
      return globals.ORGANIZATION_API;
    case globals.THING_API_NAME:
      return globals.THING_API;
    case globals.WIDGET_API_NAME:
      return globals.WIDGET_API;
    case globals.AUTHENTICATED_API_NAME:
      return globals.AUTHENTICATED_API_NAME;
    case globals.PLATFORM_API_NAME:
      return globals.PLATFORM_API;
    default:
      throw new Error(`Unknown API name: ${apiName}`);
  }
};

/**
 * State for the error handler instance
 * Using a closure to maintain state in functional style
 */
let errorHandlerInstance: ReturnType<typeof useClerkErrorHandler> | null = null;

/**
 * Initialize the error handler for API operations
 * This should be called from a React component that has access to Clerk hooks
 * @param errorHandler - The error handler instance from useClerkErrorHandler
 */
export const initializeApiErrorHandler = (errorHandler: ReturnType<typeof useClerkErrorHandler>): void => {
  errorHandlerInstance = errorHandler;
};

/**
 * Execute API operation with comprehensive error handling and retry logic
 * @param operation - The API operation to execute
 * @returns Promise<any> - API response
 */
const executeWithErrorHandling = async (operation: () => Promise<any>): Promise<any> => {
  try {
    // If error handler is available, use retry logic
    if (errorHandlerInstance) {
      return await errorHandlerInstance.retryWithTokenRefresh(operation);
    }
    
    // Fallback to direct execution
    return await operation();
    
  } catch (error) {
    logger.error('API operation failed:', error);
    
    // If error handler is available, try to handle the error
    if (errorHandlerInstance) {
      const wasHandled = await errorHandlerInstance.handleAuthError(error);
      
      if (wasHandled) {
        // Error was handled (likely user was signed out), throw a user-friendly error
        throw new Error('Authentication session expired. Please sign in again.');
      }
    }
    
    // Re-throw the original error if not handled
    const isAxiosError = axios.isAxiosError(error);
    if(isAxiosError) {
      const axiosError = error as AxiosError;
      // Check if a custom error response message is available
      const anyData = axiosError.response?.data as any;
      const customErrorMessage = anyData?.errorCode && anyData?.message;
      if (customErrorMessage) {
        error.message = customErrorMessage;
      } else {
        // Use response error message if available, otherwise use the original error message
        error.message = axiosError.response?.statusText || axiosError.message;
      }
    }

    throw error;
  }
};

/**
 * Create axios config with authentication headers
 * @param init - Request initialization options (includes body, headers, etc.)
 * @returns Promise<object> - Axios config with auth headers
 */
const createAuthenticatedConfig = async (init?: any): Promise<any> => {
  const authHeaders = await getCustomHeader();
  
  return {
    headers: {
      'Content-Type': 'application/json',
      ...authHeaders,
      ...init?.headers,
    },
    // Handle body parameter (convert to data for axios)
    ...(init?.body && { data: init.body }),
    // Pass through other axios options
    ...Object.fromEntries(
      Object.entries(init || {}).filter(([key]) => 
        !['body', 'headers', 'response'].includes(key)
      )
    ),
  };
};

/**
 * Enhanced GET request with Clerk authentication and error handling
 * @param apiName - The API name (from globals)
 * @param path - The API path
 * @param init - Additional request options
 * @returns Promise<any> - API response (wrapped to match AWS Amplify format)
 */
export const clerkApiGet = async (apiName: string, path: string, init?: any): Promise<any> => {
  return executeWithErrorHandling(async () => {
    const baseUrl = getApiBaseUrl(apiName);
    const config = await createAuthenticatedConfig(init);
    const response: AxiosResponse = await axios.get(`${baseUrl}${path}`, config);
    
    // Return format to match AWS Amplify (return whole response if response: true, otherwise just data)
    return init?.response ? response : response.data;
  });
};

/**
 * Enhanced POST request with Clerk authentication and error handling
 * @param apiName - The API name (from globals)
 * @param path - The API path
 * @param init - Request options including body
 * @returns Promise<any> - API response (wrapped to match AWS Amplify format)
 */
export const clerkApiPost = async (apiName: string, path: string, init?: any): Promise<any> => {
  return executeWithErrorHandling(async () => {
    const baseUrl = getApiBaseUrl(apiName);
    const config = await createAuthenticatedConfig(init);
    const response: AxiosResponse = await axios.post(`${baseUrl}${path}`, config.data, config);
    
    // Return format to match AWS Amplify (return whole response if response: true, otherwise just data)
    return init?.response ? response : response.data;
  });
};

/**
 * Enhanced PUT request with Clerk authentication and error handling
 * @param apiName - The API name (from globals)
 * @param path - The API path
 * @param init - Request options including body
 * @returns Promise<any> - API response (wrapped to match AWS Amplify format)
 */
export const clerkApiPut = async (apiName: string, path: string, init?: any): Promise<any> => {
  return executeWithErrorHandling(async () => {
    const baseUrl = getApiBaseUrl(apiName);
    const config = await createAuthenticatedConfig(init);
    const response: AxiosResponse = await axios.put(`${baseUrl}${path}`, config.data, config);
    
    // Return format to match AWS Amplify (return whole response if response: true, otherwise just data)
    return init?.response ? response : response.data;
  });
};

/**
 * Enhanced PATCH request with Clerk authentication and error handling
 * @param apiName - The API name (from globals)
 * @param path - The API path
 * @param init - Additional request options
 * @returns Promise<any> - API response (wrapped to match AWS Amplify format)
 */
export const clerkApiPatch = async (apiName: string, path: string, init?: any): Promise<any> => {
  return executeWithErrorHandling(async () => {
    const baseUrl = getApiBaseUrl(apiName);
    const config = await createAuthenticatedConfig(init);
    const response: AxiosResponse = await axios.patch(`${baseUrl}${path}`, config.data, config);
    
    // Return format to match AWS Amplify (return whole response if response: true, otherwise just data)
    return init?.response ? response : response.data;
  });
};

/**
 * Enhanced DELETE request with Clerk authentication and error handling
 * @param apiName - The API name (from globals)
 * @param path - The API path
 * @param init - Additional request options
 * @returns Promise<any> - API response (wrapped to match AWS Amplify format)
 */
export const clerkApiDelete = async (apiName: string, path: string, init?: any): Promise<any> => {
  return executeWithErrorHandling(async () => {
    const baseUrl = getApiBaseUrl(apiName);
    const config = await createAuthenticatedConfig(init);
    const response: AxiosResponse = await axios.delete(`${baseUrl}${path}`, config);
    
    // Return format to match AWS Amplify (return whole response if response: true, otherwise just data)
    return init?.response ? response : response.data;
  });
};

/**
 * API interceptor object with functional methods
 * Provides a convenient interface for all HTTP methods
 */
export const clerkApiInterceptor = {
  get: clerkApiGet,
  post: clerkApiPost,
  put: clerkApiPut,
  patch: clerkApiPatch,
  delete: clerkApiDelete
};

/**
 * Hook to initialize the API interceptor with Clerk error handling
 * Should be called in a component that has access to Clerk hooks
 * @returns Object with API methods
 */
export const useClerkApiInterceptor = () => {
  const errorHandler = useClerkErrorHandler();
  
  // Initialize the error handler
  React.useEffect(() => {
    initializeApiErrorHandler(errorHandler);
  }, [errorHandler]);
  
  return clerkApiInterceptor;
};
